/**
 * tuning.js —— 所有可调参数集中在这里。
 *
 * 规则：场景代码里**不许**出现魔法数字，一律从这里取。
 * Codex 迭代视觉时只改这个文件，改完截图对比 scene/ref-plane.png。
 *
 * 带 [实测] 的是从参考录屏量出来的，改动前先看 VISUAL_SPEC.md 的量法。
 * 带 [待调] 的是需要在真机上试出来的。
 */

export const COLOR_THEMES = {
  jade: {
    primary: 0x7fe3c0,       // [实测] 旧版参考·青玉绿
    highlight: 0xdffbee,     // [实测] 旧版参考·玉白
    glowInner: 0x0e1c17,     // [实测]
    glowMid: 0x080e0c,       // [实测]
    star: 0xc8e0d6
  },
  azure: {
    primary: 0xa8c8f0,       // [实测] V2 新参考·冷蓝白
    highlight: 0xe8f2ff,     // [实测] V2 新参考·冰白高光
    glowInner: 0x1a2838,     // [实测] V2 新参考·中心蓝辉光
    glowMid: 0x0b121c,       // [待调] 蓝辉光向背景的中段过渡
    star: 0xc9dbf2
  }
};

const requestedTheme = typeof window === 'undefined'
  ? null
  : new URLSearchParams(window.location.search).get('theme');
export const ACTIVE_THEME = requestedTheme === 'jade' ? 'jade' : 'azure';
const theme = COLOR_THEMES[ACTIVE_THEME];

export const COLORS = {
  background: 0x04060a,      // [实测]
  kioskBackground: 0x000000, // [实测] 语音召唤模式要求纯黑全屏
  glowInner: theme.glowInner,
  glowMid: theme.glowMid,
  primary: theme.primary,
  highlight: theme.highlight,
  /** 兼容既有场景命名；二者随 ACTIVE_THEME 切换，并不恒指青绿色。 */
  jade: theme.primary,
  jadeBright: theme.highlight,
  taiji: 0xffffff,           // [实测] 太极核心
  gold: 0xffe98a,            // [实测] 旋涡金
  crimson: 0xff8f6b,         // 动爻赤（原版没有，是我们加的）
  star: theme.star
};

/** 世界空间定向高光；模拟环形带面，而不是给贴图烘焙一道固定亮弧。 */
export const LIGHT = {
  direction: { x: 0.4, y: 0.6, z: 0.7 }, // [待调] V8：右上前方固定光源
  specStrength: 8.00,                     // [实测] V8：0.7R 峰均比 2.19（目标边界约 2.2）
  shininess: 24,                          // [待调] V8：高光集中程度
  ringTilt: 0.35,                         // [待调] V8：环形带面的基础法线倾斜
  innerTiltScale: 0.62,                   // [待调] 内圈较平
  outerTiltScale: 1.28,                   // [待调] 外圈较立
  innerStrengthScale: 0.68,               // [待调] 内圈高光较弱
  outerStrengthScale: 1.18,               // [待调] 外圈高光较强
  alphaCutoff: 0.001                       // [实测] 跳过完全透明片元
};

export const FIELD = {
  /** 最外圈（节气环）的世界半径，其余层按 RING_SPECS.radius 比例缩放 */
  outerRadius: 5.6,           // [实测] T1：最外圈占视口短边约 82%
  /** 立体态的锥形系数：z = -cone * radius^coneExponent * outerRadius */
  cone: 1.05,                // [实测] V8 手势观感；T2 静态椭圆拟合值为 0.6285
  /** 锥形曲线指数：环层与粒子共用 */
  coneExponent: 2.553,       // [实测] T2：指数范围 [1.2, 4.0] 网格搜索
  /** 平面态六层共面，留极小间隔避免 z-fighting */
  planeGap: 0.004,
  /** 贴图分辨率：桌面 / 移动端 */
  textureSize: 1024,
  textureSizeMobile: 512,
  /** 高亮层只有少量格子，半分辨率足够且可把额外显存压到四分之一。 */
  highlightTextureScale: 0.5 // [实测] 桌面 512² / 移动端 256²
};

/**
 * V2 罗盘的十二道文字环（加中心太极共十三层）。
 * 去掉与卦名重复的六十四卦符号环，外圈间距逐步放大，形成通透的罗盘层级。
 * tokens 只负责内容，所有几何、字重、透明度与转速都集中在这里。
 * idleSpin 是每层的基准交叉转速；各状态只通过 SPIN 倍率改变整体节奏。
 * 成卦瞬间归零校准，短暂停住后仍恢复旋转。
 */
export const RING_LAYOUT = [
  // Canvas 方位：-π/2=屏幕上/南，0=屏幕右/西，π/2=屏幕下/北，π=屏幕左/东。
  { key: 'trigramSymbols', radius: 0.270, inner: 0.385, outer: 0.470, fontScale: 0.563, textOpacity: 0.85, weight: 1.08, opacity: 0.85, idleSpin:  0.120, startAngle: -Math.PI / 2 }, // [待调] V12：最内层顺时针；成卦后离南归位
  { key: 'trigrams',      radius: 0.350, inner: 0.395, outer: 0.470, fontScale: 0.546, textOpacity: 0.85, weight: 1.02, opacity: 0.85, idleSpin: -0.113, startAngle: -Math.PI / 2 }, // [待调] V12：与相邻层反向；成卦后与卦符逐格同位
  { key: 'nineStars',     radius: 0.410, inner: 0.415, outer: 0.470, fontScale: 0.292, textOpacity: 0.85, weight: 0.94, opacity: 0.85, idleSpin:  0.106, startAngle: -Math.PI / 2, preserveTextureColors: true }, // [待调] V12：飞星环空闲转动、成卦归真实八宫方位
  { key: 'qimenStars',    radius: 0.460, inner: 0.418, outer: 0.470, fontScale: 0.282, textOpacity: 0.75, weight: 0.86, opacity: 0.60, idleSpin: -0.100, startAngle: -Math.PI / 2 }, // [待调] 仅形制展示，不是奇门盘局
  { key: 'gates',         radius: 0.510, inner: 0.422, outer: 0.470, fontScale: 0.458, textOpacity: 0.75, weight: 0.80, opacity: 0.60, idleSpin:  0.093, startAngle: -Math.PI / 2 }, // [待调] 仅形制展示，不是奇门盘局
  { key: 'stems',         radius: 0.560, inner: 0.425, outer: 0.470, fontScale: 0.440, textOpacity: 0.75, weight: 0.74, opacity: 0.60, idleSpin: -0.086, startAngle: -Math.PI / 2 }, // [待调] 十干展示序列，无均分方位语义
  { key: 'branches',      radius: 0.610, inner: 0.427, outer: 0.470, fontScale: 0.440, textOpacity: 0.75, weight: 0.68, opacity: 0.60, idleSpin:  0.079, startAngle: Math.PI / 2 }, // [待调] V12：空闲转动；成卦后子归正北
  { key: 'mountains',     radius: 0.660, inner: 0.429, outer: 0.470, fontScale: 0.422, textOpacity: 0.75, weight: 0.60, opacity: 0.60, idleSpin: -0.072, startAngle: Math.PI * 5 / 12 }, // [待调] V12：空闲转动；成卦后子与地支子对齐
  { key: 'terms',         radius: 0.710, inner: 0.431, outer: 0.470, fontScale: 0.352, textOpacity: 0.75, weight: 0.54, opacity: 0.60, idleSpin:  0.065, startAngle: Math.PI * 3 / 4 }, // [待调] V12：空闲转动；成卦后四至归位
  { key: 'mansions',      radius: 0.780, inner: 0.428, outer: 0.470, fontScale: 0.418, textOpacity: 0.65, weight: 0.40, opacity: 0.35, idleSpin: -0.059, startAngle: Math.PI, anchorMode: 'sector-start' }, // [工程约定] 角宿起点在正东（卯中）、顺时针；非古法定论，待可靠古籍证据
  { key: 'ganzhi',        radius: 0.880, inner: 0.425, outer: 0.470, fontScale: 0.301, textOpacity: 0.65, weight: 0.36, opacity: 0.33, idleSpin:  0.052, startAngle: Math.PI / 2 }, // [待调] V12：甲子归位正北
  { key: 'hexagrams',     radius: 1.000, inner: 0.415, outer: 0.470, fontScale: 0.284, textOpacity: 0.65, weight: 0.32, opacity: 0.32, idleSpin: -0.045, startAngle: -Math.PI / 2 }  // [待调] V12：最外层最低反向转速
];

/** 中心太极相对最外圈的半径；与前两道八卦环共同占据约 35% 内盘。 */
export const TAIJI_RADIUS = 0.176; // [实测] V9：三态投影占盘面短边 23.87–24.33%

/** 中心扩散辉光：Sprite 是始终面向相机的径向渐变平面。 */
export const CENTER_GLOW = {
  radiusScale: 0.50,         // [实测] V10：三态辉光中心偏移 1.14–2.35%，不再溢到盘外抢重心
  textureSize: 512,          // [实测] 径向渐变无需环贴图同等分辨率
  midStop: 0.42,             // [实测] glowInner 向 glowMid 的过渡位置
  opacity: ACTIVE_THEME === 'azure' ? 8.0 : 48.0, // [实测] V2 蓝白版保留太极线条；旧青玉版维持原辉光
  awakenWeight: 0.72,        // [实测] 仅唤醒阵眼时的辉光强度
  vortexOpacityBoost: 0.35,  // [实测] P2：旋涡强光团保留内圈文字轮廓
  vortexScaleBoost: 0.30,    // [实测] P2：旋涡态辉光向外扩张
  pulseBase: 0.94,           // [实测] 呼吸亮度基线
  pulseAmplitude: 0.06,      // [实测] 呼吸幅度
  pulseSpeed: 1.2,           // [实测] 呼吸速度
  depthOffset: 0,            // [实测] V8：与锥顶太极同处 z=0，避免光核与光晕分离
  renderOrder: 5             // [实测] 星场之后、六层环之前
};

/** 环纹理与材质的共同风格参数。 */
export const RING_STYLE = {
  fontFamily: '"Songti SC Light", "STSong", "Noto Serif CJK SC Light", serif', // [实测] V2：优先细宋体
  glowRatio: 0.0035,         // [实测] P2：收窄贴图阴影，避免细描边重新糊成粗字
  textStrokeRatio: 0.06,     // [实测] 封版回调：6% 字号描边兼顾细体与可读性
  textOpacity: 0.60,         // [实测] 无分层值时的中层透明度
  radialTextGap: 0.92,       // [实测] 多字词沿半径由内向外排列的字距
  materialBrightMix: 0.45,   // [实测] P0：补足峰值红通道，仍保留区域青玉色相
  opacityScale: ACTIVE_THEME === 'azure' ? 1.0 : 1.15, // [实测] V2 整体减重；旧青玉版维持原亮度
  aimOpacity: 0.25,         // [待调] V13：唤醒后全盘先以低亮度出现，落爻层再逐层提亮
  vortexOpacityBoost: 0.34,  // [实测] P2：旋涡开启时所有环同步提亮
  edgeWeight: 0.8,           // [实测] V2：静态环内外边界约 0.8px 级细线
  edgeOpacity: 0.40,         // [实测] V2：主边界线透明度
  detailLineWeight: 0.42,    // [实测] V2：装饰平行线比主边界再细一档
  detailLineOpacity: 0.15,   // [实测] V2：装饰线只提供层次，不抢文字
  detailLineOffsets: [0.08, 0.16], // [实测] 从环带内外边界各内缩两道平行线
  textureAnisotropy: 4,      // [实测] 倾斜态仍保持细字可读
  revealScaleMin: 0.12,      // [实测] 从中心逐层展开的起始缩放
  colorLerp: 0.08,           // [实测] 环色渐变平滑系数
  movingPulseBase: 0.72,     // [实测] 动爻高亮脉动基线
  movingPulseAmplitude: 0.28,// [实测]
  movingPulseSpeed: 3.4      // [实测]
};

/**
 * 八卦符号不用 Unicode 字形，统一由 Canvas 三条爻线绘制。
 * 比例均相对传入的 glyph 宽高，保证浏览器与离线贴图完全一致。
 */
export const TRIGRAM_GLYPH = {
  widthScale: 0.92,          // [实测] 卦符相对该层字号的横向占比
  heightScale: 1.36,         // [实测] 三爻总高度，保持内圈符号有纵向重心
  lineWidthRatio: 0.13,      // [实测] 单爻线宽（相对 glyph 高度）
  lineSpacingRatio: 0.37,    // [实测] 相邻爻中心距（相对 glyph 高度）
  yinGapRatio: 0.24          // [实测] 阴爻中央断口（相对 glyph 宽度）
};

/** 成卦后的数据格高亮。底图不改色，高亮层按状态分别叠加。 */
export const CELL_HIGHLIGHT = {
  layerKeys: ['trigramSymbols', 'trigrams', 'stems', 'branches', 'terms', 'ganzhi', 'hexagrams'],
  pulseLayerKeys: ['stems', 'branches'],
  fillOpacity: 0,            // [实测] V15 回退：所有格底完全透明，高亮只保留描边与文字
  strokeOpacity: 0.68,       // [实测] V2：格框存在但不抢主体
  textOpacity: 0.90,         // [实测] 被选中文字亮度
  lineWidthScale: 1.22,      // [实测] 高亮边线相对底图线宽
  glowRatio: 0.010,          // [待调] 高亮贴图自身柔光
  depthOffset: 0.006,        // [实测] 高亮层略靠近相机，避免 z-fighting
  pulseDepthOffset: 0.009,   // [实测] 动爻脉动层再前置一级
  opacity: 1.0,              // [待调] 静态高亮材质总透明度
  pulseOpacity: 1.15,        // [待调] 动爻赤色峰值
  pulseBase: 0.58,           // [待调]
  pulseAmplitude: 0.42,      // [待调]
  pulseSpeed: 4.2,           // [待调]
  useGodBoost: 1.24,         // [待调] 用神格比普通纳甲格再亮一档
  voidDash: [7, 5],          // [待调] 旬空格虚线框
  connectionInset: 0.12,     // [待调] 本卦到变卦的环内连线位置
  connectionOpacity: 0.75,   // [待调]
  connectionInsets: {
    useGodToShi: 0.12,
    overcome: 0.22,
    generate: 0.32,
    transform: 0.42
  },                         // [待调] 多类关系线错层，避免重叠成一条
  timingMinBoost: 0.34,      // [待调] 低置信度候选仍可见但明显更暗
  timingMaxBoost: 1.08,      // [待调] 高置信度候选略高于普通高亮
  revealDuration: 0.52,      // [待调] 成卦数据高亮逐层显现
  layerStagger: 0.055,       // [待调] 与摇卦涟漪同方向，由内向外
  renderOrderBase: 24,       // [实测] 位于底环之上、太极与旋涡之下
  colors: {
    normal: COLORS.jadeBright,
    primary: COLORS.jadeBright,
    changed: COLORS.gold,
    moving: COLORS.crimson,
    useGod: COLORS.jadeBright,
    month: COLORS.gold,
    day: COLORS.jadeBright,
    term: COLORS.jadeBright,
    void: COLORS.jadeBright,
    useGodToShi: COLORS.crimson,
    overcome: COLORS.crimson,
    generate: COLORS.jade,
    transform: COLORS.gold,
    timing: COLORS.gold,
    ti: COLORS.jadeBright,
    yong: COLORS.gold
  }
};

/** 应期格悬停卡片；置信度只表示规则可靠度，不表示事件发生概率。 */
export const TIMING_TOOLTIP = {
  offsetX: 16,               // [待调]
  offsetY: 18,               // [待调]
  maxWidth: 320              // [待调]
};

/** 新增模式面板的布局参数，通过 CSS 变量注入页面。 */
export const APP_UI = {
  modeTop: 82,               // [待调]
  modeLeft: 26,              // [实测] 与左上 HUD 对齐
  modeGap: 8,                // [待调]
  modePanelWidth: 286        // [待调]
};

/** URL display 参数对应的短边占比；只改变取景距离，不改变十三层几何。 */
export const VIEWPORT_FILL = {
  standard: 0.82,            // [实测] 默认构图
  full: 0.90                 // [实测] ?display=full 语音召唤构图
};

/** 梅花模式只保留内圈八卦，并把内盘放大到接近六爻盘的主体尺度。 */
export const MEIHUA_VIEW = {
  layerKeys: ['trigramSymbols', 'trigrams'],
  scale: 2.45,               // [待调] 两道八卦环占据约 80% 短边
  particleOpacityScale: 0.34,// [待调] 保留少量气机但不干扰体用两卦
  glowOpacityScale: 0.62     // [待调] 放大后压住中心辉光，避免过曝
};

/** 最外圈匀速扫描光带。 */
export const SCAN_BAND = {
  period: 8.0,               // [实测] V2：约 8 秒一周
  angle: Math.PI / 6,        // [实测] 30° 亮弧
  radialWidth: 0.018,        // [实测] V2：细弧，不形成白色粗条
  opacity: 0.52,             // [实测]
  brightness: 1.35,          // [实测] 略过 bloom 阈值
  segments: 72,              // [实测] 30° 弧段平滑度
  trailSegments: 8,          // [实测] 分段透明度模拟渐隐拖尾
  trailPower: 2.2,           // [实测] 弧头亮、弧尾柔和
  depthOffset: 0.010,        // [实测] 避免与最外环共面闪烁
  startAngle: -Math.PI / 2,  // [实测] 首帧从顶部开始
  renderOrder: 38            // [实测]
};

export const TAIJI_STYLE = {
  opacityBase: 1.0,          // [实测] V14：AIM 起即全亮；展开增强交给中心辉光和完成闪光
  opacityPulse: 0.0,         // [实测] V14：太极本体不再因呼吸跌成灰色
  pulseSpeed: 1.6,           // [实测] 太极呼吸速度
  materialBrightness: 1.0,   // [实测] V14：白半保持纯白，S 形暗边由独立描边保留
  darkColor: '#0e1218',      // [实测] V11：阴鱼深灰蓝，明显区别背景 #04060a
  strokeWidthRatio: 0.015,   // [待调] V12：太极外圆与 S 线均为半径的 1.5%
  strokeAlpha: 0.92,         // [待调] V12：发光线稿主体透明度
  yangAlpha: 1.0,            // [实测] V14：阳鱼恢复纯白，不再像灰色贴片
  yinAlpha: 0.45,            // [待调] V12：阴鱼半透明深色
  eyeRadiusRatio: 1 / 9,     // [待调] V12：鱼眼缩至 r/9
  glowBlur: 0.09,            // [待调] V12：线稿外发光半径相对太极半径
  renderOrder: 50,           // [实测] V11：高于环、高亮、扫描带与旋涡，防止阵眼被覆盖
  minScale: 0.6,             // [实测] 唤醒前的缩放下限
  vortexScaleBoost: 0.35,    // [实测] P2：旋涡态阵眼膨胀
  vortexOpacityBoost: 0.10   // [实测] V8：旋涡时仍增亮，但不再烧掉 S 形
};

/** 六次落爻的视觉仪式；只映射已有爻值，不参与排盘判断。 */
export const CAST_RITUAL = {
  layerKeys: ['trigramSymbols', 'trigrams', 'nineStars', 'stems', 'branches', 'mountains'], // [待调] V12：初爻至上爻由内向外
  angularSpeed: 5.0,         // [待调] V13：落爻层提亮期间快速自转 rad/s
  waveDuration: 0.30,        // [待调] V12：定格光环扩散时长
  waveExpansion: 0.34,       // [待调] V12：光环从该层再向外扩散 34%
  waveOpacity: 0.80,         // [待调] V12：定格闪环初始亮度
  waveThickness: 0.018,      // [待调] V12：世界空间环波线宽
  waveSegments: 160,         // [待调] V12：环波圆滑度
  waveDepthOffset: 0.026,    // [待调] V12：避免与盘面 z-fighting
  waveRenderOrder: 46,       // [待调] V12：高于盘面，低于太极
  completionScale: 1.06,     // [待调] V12：六爻满后全盘一次外推
  completionDuration: 0.40,  // [待调] V12：外推往返总时长
  taijiFlashScale: 0.16,     // [待调] V12：成卦瞬间太极强闪缩放
  glowFlashBoost: 0.75       // [待调] V12：中心辉光强闪增益
};

export const CAMERA = {
  fov: 50,                   // [实测] T2：与参考帧透视强度对齐
  near: 0.1,
  far: 200,
  distance: 13.0,            // [实测] V14：平面态外圈直径占视口短边约 92%
  depthFramingCompensation: 0.40, // [实测] V14：补偿透视收缩，同时为倾斜近侧保留边距
  portraitDistanceScale: 1.14,// [实测] 手机竖屏额外留 12% 边缘，避免倾斜时黄色外圈被裁
  compactLandscapeDistanceScale: 1.16,// [实测] iPhone 横屏左侧窄画布留边，保证倾斜外圈不越界
  restTilt: { x: 0.28, y: 0.06, z: 0 }, // [实测] V8：静止态也形成可见明暗面
  restDrift: {
    period: 45,              // [实测] V8：45 秒完成一次极慢呼吸
    amplitudeX: 0.05,        // [实测] V8：x 方向 ±0.05rad
    amplitudeY: 0.05,        // [实测] V8：y 方向 ±0.05rad
    phaseX: 0,               // [实测] V8：两轴错相，避免机械式对角摆动
    phaseY: Math.PI / 2
  },
  referenceTilt: { x: -0.32, y: 0.18, z: -0.02 }, // [实测] V2 倾斜参考的绝对姿态
  /** 掌心控向的最大偏转，超过这个角度文字会侧到看不清 */
  maxTiltX: 0.55,            // [实测] V8：最大手势偏转的立体观感
  maxTiltY: 0.55,            // [实测] V8：最大手势偏转的立体观感
  maxRollZ: 0.75,            // [实测] V8：最大手势滚转
  palmNeutralX: 0.50,        // [实测] 2026-09-01：掌心自然位水平居中
  palmNeutralY: 0.8410,      // [实测] 2026-09-01 真人自然手位：debug 连续帧 palm cy=0.8410
  palmPitchBias: 0,          // [实测] 2026-09-01：诊断期间禁用补偿，先核对映射符号
  measurePoseScale: 0.48,    // [实测] V8：手仍完整入镜时的有效输入边界；投影偏移约 0.42R
  /** 手势输入的平滑系数，越小越跟手、越抖 */
  smoothing: 0.09            // [实测] V8：降低平滑量，提高跟手性
};

/** 唤醒态：食指指尖只负责平移阵眼，不参与盘面转向。 */
export const AIM = {
  followRangeX: 0.30,        // [待调] AIM：阵眼水平可移动范围，占视口宽度的比例
  followRangeY: 0.30,        // [待调] AIM：阵眼垂直可移动范围，占视口高度的比例
  smoothing: 0.09            // [实测] AIM：沿用 V8 手势平滑系数
};

/** 手机触摸只产生语义事件，不参与排盘。 */
export const TOUCH = {
  centerRadius: 0.28,        // 短边占比；休眠态只响应中心点击
  tapMovePx: 14,
  longPressMs: 720,
  outwardSwipeRatio: 0.18,   // 从中心向外滑动的最小短边占比
  pinchRatio: 0.78,          // 双指间距收缩到起始值的 78% 触发变卦
  rotateScale: 1.7,
  shakeThreshold: 22,
  shakeCooldownMs: 900
};

export const TIMING = {
  awaken: 0.75,              // [实测] 阵眼唤醒
  expandDuration: 1.60,      // [待调] 张掌后十二层从阵眼向外全部展开的总时长
  expandStagger: 0.08,       // [待调] 相邻层由内向外展开的延迟
  expandOvershoot: 1.04,     // [待调] 每层展开末段的短暂尺寸过冲
  planeExpand: 0.55,         // [待调] V10：收紧成卦后的平面展开节奏
  layerStagger: 0.04,        // [待调] V10：收紧逐层展开间隔
  toDepth: 0.7,              // [待调] V10：收紧成卦后的立体展开节奏
  toPlane: 0.83,             // [实测] T3：60fps gold/extent 10%–90% 反解 ease-in
  vortexOn: 0.63,            // [实测] T3：60fps gold 10%–90% 反解 ease-out
  /** 每摇一爻：该层旋转一周并定住 */
  castSpin: 0.35,            // [待调] V10：每爻点亮定住时长
  /** 成卦后的定格停顿 */
  settle: 0.25,              // [待调] V10：六爻满后到成卦的停顿
  formationSoundLead: 1.45,  // [实测] Demo.1 有效切点后到主峰；第六爻定格后于主峰显卦
  snapToPosition: 0.80,      // [待调] V12：成卦时各环按最短路径归真实方位
  formedHoldDuration: 1.50   // [待调] V13：方位校准后的唯一静止窗口
};

/** 各状态对 RING_LAYOUT.idleSpin 的倍率；只有 formedHold 为静止。 */
export const SPIN = Object.freeze({
  aim: 2.5,                 // [待调] V13：唤醒态交叉旋转基准
  casting: 2.5,             // [待调] V13：未定格层保持基准转速
  formedHold: 0,            // [待调] V13：成卦方位校准后定格 1.5 秒
  formed: 1.2,              // [待调] V13：定格后缓慢旋转
  vortex: 4.5               // [待调] V13：变卦旋涡态加速
});

export const PARTICLES = {
  count: 320,
  countMobile: 120,
  radiusMin: 1.2,
  radiusMax: 5.2,
  angularMin: 0.12,
  angularMax: 0.40,
  zJitter: 0.9,
  size: 0.055,
  opacity: 0.55,             // [实测] 常态粒子亮度
  vortexOpacityBoost: 0.30,  // [实测] P2：旋涡态粒子同步提亮
  vortexAngularBoost: 2.2,   // [实测] P2：旋涡态角速度增益
  vortexInflowBoost: 1.6,    // [实测] P2：旋涡态内流增益
  resetRadiusScale: 0.35,    // [实测] 粒子抵达阵眼后的回卷半径
  /** 立体态时向中心汇聚的速度 */
  inflow: 0.30
};

export const STARS = {
  count: 520,
  countMobile: 260,
  spread: 60,
  depth: [-40, -12],
  size: 1.6,
  opacityMin: 0.15,
  opacityMax: 0.75,
  parallax: 0.015
};

export const VORTEX = {
  /** 相对最外圈的半径倍数 [实测：约 1.15] */
  radiusScale: 1.06,         // [实测] V2：完整收进画面且仍形成外部边界
  tube: 0.030,               // [实测] P3：细化为边界线
  segments: 220,
  opacity: 0.62,             // [实测] P3：细金环的最终不透明度
  brightness: 2.4,           // [实测] P3：线性亮度越过 bloom threshold
  rotationSpeed: 0.22,       // [实测] 旋涡金环自转速度
  /** 开启时的一次冲击波扩散倍数 */
  shockwave: 1.9
};

export const BLOOM = {
  strength: 0.35,            // [实测] T4：白纹理修复后重标定
  radius: 0.25,              // [实测] T4：收窄光晕，避免金环洗成白带
  threshold: 0.75            // [实测] T4：白纹理修复后重标定
};

/** 手势判定阈值 —— 必须用真手在真摄像头前标定，原版的数不一定适合你的手 */
export const GESTURE = {
  extendedRatio: 1.30,       // [实测] 2026-08-28 真人标定 1749 帧；1077 帧标注样本误判 0、漏判 0.09%
  curledRatio: 1.12,         // [实测] 2026-08-28 真人标定 1749 帧；1077 帧标注样本误判 0、漏判 0.09%
  /** 连续多少帧一致才确认手势，防抖 */
  stableFrames: 3,           // [待调] V10：三帧连续一致即确认
  /** 握拳→张开的最小间隔（秒），防止一次动作被判成两次 */
  cooldown: 0.25,            // [待调] V10：手势确认最小间隔
  /** MediaPipe 推理帧率，渲染仍跑满 60 */
  inferenceFps: 22,
  /** iOS Safari 的 GPU delegate 可能既不 resolve 也不 reject。 */
  gpuCreateTimeoutMs: 8000
};

export const QUALITY = {
  isMobile: typeof navigator !== 'undefined' && /Android|iPhone|iPad/i.test(navigator.userAgent || ''),
  /** renderer CSS 尺寸与视口的允许误差 [实测] */
  resizeTolerancePx: 1,
  /** ?measure=1 固定用 DPR 2，避免验收截图因宿主视口降到 DPR 1 而整体变暗。 */
  measurementPixelRatio: 2, // [实测] 与 V8 截图口径一致
  /** 移动端关掉后期 bloom，改用贴图自带 glow */
  bloomOnMobile: false
};

/** 仅在 ?measure=1 时输出一次真实渲染帧率。 */
export const PERFORMANCE = {
  sampleDurationMs: 3000,    // [待调] 手机首次进入的自动分档窗口
  millisecondsPerSecond: 1000,
  maxDeltaSeconds: 0.05,     // [实测] 恢复后台标签时避免动画跳跃
  tierOneFps: 45,
  tierTwoFps: 30,
  tierOnePixelRatio: 1.25,
  tierTwoPixelRatio: 1,
  tierTwoLayerKeys: [
    'trigramSymbols', 'trigrams', 'nineStars', 'gates',
    'stems', 'branches', 'mountains', 'terms'
  ]
};

/** 只在 ?debug=1 出现的手势标定界面参数。 */
export const DEBUG_UI = {
  previewWidth: 200,         // [实测] 标定时可看清整只手且不遮挡主体
  previewAspect: 4 / 3,      // [实测] 与摄像头 640×480 一致
  previewInset: 18,          // [实测] 与调试面板边距对齐
  previewBufferWidth: 640,   // [实测] 骨架画布分辨率
  boneLineWidth: 2,          // [待调] 真机标定时确认可见性
  boneOpacity: 0.78,         // [待调]
  landmarkRadius: 3,         // [待调]
  landmarkOpacity: 0.95      // [待调]
};
